import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  Switch,
  Tooltip,
  Snackbar,
  FormControlLabel,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import SystemAlert from '../../common/SystemAlert';
import { API_URL } from '../../common/Constants';
import Cookies from '../../common/Cookies';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const TeamManagement = () => {
  const tenantId = Cookies.getTenantId();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [agencyUsers, setAgencyUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [agencyLoading, setAgencyLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [inviteForm, setInviteForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    accessType: 'standard'
  });
  const [editForm, setEditForm] = useState({
    id: '',
    firstName: '',
    lastName: '',
    email: '',
    accessType: 'standard'
  });
  const [emailError, setEmailError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [ownerChangeDialogOpen, setOwnerChangeDialogOpen] = useState(false);
  const [pendingAccessType, setPendingAccessType] = useState(null);

  // Fetch users for tenant
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/users/list`, { params: { tenant_id: tenantId } });
      const mappedUsers = (res.data || []).map(user => ({
        ...user,
        firstName: user.first_name,
        lastName: user.last_name
      }));
      setUsers(mappedUsers);
      console.log('Mapped users:', mappedUsers);
    } catch (err) {
      setAlert({ severity: 'error', message: 'Failed to load team members.' });
    } finally {
      setLoading(false);
    }
  };
  const fetchAgencyUsers = async () => {
    setAgencyLoading(true);
    try {
      const res = await axios.get(`${API_URL}/users/agency-users`, { params: { tenant_id: tenantId } });
      const mapped = (res.data || []).map(user => ({
        ...user,
        firstName: user.first_name,
        lastName: user.last_name
      }));
      setAgencyUsers(mapped);
    } catch (err) {
      setAgencyUsers([]);
    } finally {
      setAgencyLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchAgencyUsers();
  }, [tenantId]);

  // Invite modal handlers
  const handleInviteOpen = () => {
    setInviteForm({ firstName: '', lastName: '', email: '', accessType: 'standard' });
    setEmailError('');
    setInviteDialogOpen(true);
  };
  const handleInviteClose = () => {
    setInviteDialogOpen(false);
  };

  // Edit modal handlers
  const getUserAccessType = (user) => {
    if (user.owner) return 'owner';
    if (user.chat_log_access_only) return 'chatLogOnly';
    return 'standard';
  };

  const handleEditOpen = (user) => {
    setEditForm({
      id: user.id,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      accessType: getUserAccessType(user)
    });
    setEditDialogOpen(true);
  };
  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditForm({ id: '', firstName: '', lastName: '', email: '', accessType: 'standard' });
  };

  // Delete modal handlers
  const handleDeleteOpen = (user) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };
  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setSelectedUser(null);
  };

  // Email uniqueness check
  const checkEmailExists = async (email) => {
    try {
      // GET /users/emailExists?email=...&tenant_id=...
      const res = await axios.get(`${API_URL}/users/emailExists`, { params: { email, tenant_id: tenantId } });
      return res.data.exists;
    } catch {
      return false;
    }
  };

  // Helper to generate a random password
  const generateRandomPassword = (length = 12) => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return password;
  };

  // Invite submit
  const handleInviteSubmit = async () => {
    setEmailError('');
    // Debug log for tenantId
    console.log('Tenant ID being sent:', tenantId);
    if (!inviteForm.firstName || !inviteForm.lastName || !inviteForm.email) {
      setEmailError('All fields are required.');
      return;
    }
    // Validate tenantId
    if (!tenantId || tenantId === '0' || tenantId === 0) {
      setAlert({ severity: 'error', message: 'Invalid tenant ID. Please log in again or contact support.' });
      return;
    }
    // Email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(inviteForm.email)) {
      setEmailError('Invalid email address.');
      return;
    }
    // Unique email check
    const exists = await checkEmailExists(inviteForm.email);
    if (exists) {
      setEmailError('Email already exists for this tenant.');
      return;
    }
    try {
      // POST /users/invite
      await axios.post(`${API_URL}/users/invite`, {
        first_name: inviteForm.firstName,
        last_name: inviteForm.lastName,
        email: inviteForm.email,
        tenant_id: tenantId,
        status: 'i',
        password: generateRandomPassword(),
        owner: inviteForm.accessType === 'owner',
        chat_log_access_only: inviteForm.accessType === 'chatLogOnly',
        inviter_first_name: currentUser?.firstName,
        inviter_last_name: currentUser?.lastName
      });
      setAlert({ severity: 'success', message: 'Invitation sent.' });
      setInviteDialogOpen(false);
      setSnackbarOpen(true);
      // Refresh user list
      await fetchUsers();
    } catch (err) {
      setAlert({ severity: 'error', message: 'Failed to send invitation.' });
    }
  };

  // Edit submit
  const handleEditSubmit = async () => {
    if (!editForm.firstName || !editForm.lastName || !editForm.email) {
      setAlert({ severity: 'error', message: 'All fields are required.' });
      return;
    }
    const userToEdit = users.find(u => u.id === editForm.id);
    const isEditingSelf = editForm.email === currentUser?.email;
    if (isEditingSelf && userToEdit?.owner && editForm.accessType !== 'owner') {
      setAlert({ severity: 'error', message: 'You cannot remove owner status from yourself.' });
      return;
    }
    const owner = isEditingSelf ? !!userToEdit?.owner : editForm.accessType === 'owner';
    const chatLogAccessOnly =
      isEditingSelf ? !!userToEdit?.chat_log_access_only : editForm.accessType === 'chatLogOnly';
    try {
      // PUT /users/update/:id
      await axios.put(`${API_URL}/users/update/${editForm.id}`, {
        first_name: editForm.firstName || '',
        last_name: editForm.lastName || '',
        email: editForm.email,
        tenant_id: tenantId,
        owner,
        chat_log_access_only: chatLogAccessOnly,
        status: userToEdit?.status || 'a'
      });
      setAlert({ severity: 'success', message: 'User updated.' });
      setEditDialogOpen(false);
      setSnackbarOpen(true);
      // Refresh user list
      await fetchUsers();
    } catch (err) {
      setAlert({ severity: 'error', message: 'Failed to update user.' });
    }
  };

  // Delete submit
  const handleDeleteSubmit = async () => {
    try {
      // DELETE /users/:id
      await axios.delete(`${API_URL}/users/${selectedUser.id}`);
      setAlert({ severity: 'success', message: 'User deleted.' });
      setDeleteDialogOpen(false);
      setSnackbarOpen(true);
      // Refresh user list
      await fetchUsers();
    } catch (err) {
      setAlert({ severity: 'error', message: 'Failed to delete user.' });
    }
  };

  // Status toggle
  const handleStatusToggle = async (user) => {
    if (user.owner) return; // No toggle for owner
    if (user.status === 'i') return; // Can't toggle invited
    const newStatus = user.status === 'a' ? 'd' : 'a';
    try {
      // PATCH /users/status/:id
      await axios.post(`${API_URL}/users/status?userId=${user.id}&status=${newStatus}`);
      setAlert({ severity: 'success', message: 'Status updated.' });
      setSnackbarOpen(true);
      // Refresh user list
      await fetchUsers();
    } catch (err) {
      setAlert({ severity: 'error', message: 'Failed to update status.' });
    }
  };

  // Resend Invite handler (updated for new endpoint)
  const handleResendInvite = async (user) => {
    if (user.status !== 'i') {
      setAlert({ severity: 'error', message: 'Only invited users can be resent an invite.' });
      return;
    }
    try {
      await axios.post(`${API_URL}/users/resend-invite`, {
        email: user.email,
        tenantId: tenantId // always use context value
      });
      setAlert({ severity: 'success', message: 'Invite resent successfully.' });
      setSnackbarOpen(true);
      await fetchUsers();
    } catch (err) {
      setAlert({ severity: 'error', message: 'Failed to resend invite.' });
    }
  };

  const handleAccessTypeRadioChange = (e) => {
    const newType = e.target.value;
    const wasOwner = editForm.accessType === 'owner';
    const willBeOwner = newType === 'owner';
    if (wasOwner !== willBeOwner) {
      setPendingAccessType(newType);
      setOwnerChangeDialogOpen(true);
      return;
    }
    setEditForm((prev) => ({ ...prev, accessType: newType }));
  };

  const handleOwnerChangeConfirm = () => {
    if (pendingAccessType != null) {
      setEditForm((prev) => ({ ...prev, accessType: pendingAccessType }));
    }
    setPendingAccessType(null);
    setOwnerChangeDialogOpen(false);
  };
  const handleOwnerChangeCancel = () => {
    setPendingAccessType(null);
    setOwnerChangeDialogOpen(false);
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mt: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Team Management</Typography>
        <Button variant="contained" color="primary" onClick={handleInviteOpen}>
          Invite Team Member
        </Button>
      </Box>
      {alert && (
        <SystemAlert open={!!alert} severity={alert.severity} message={alert.message} onClose={() => setAlert(null)} />
      )}
      <TableContainer sx={{ minWidth: 1200 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Username/Email</TableCell>
              <TableCell>First Name</TableCell>
              <TableCell>Last Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5}>Loading...</TableCell></TableRow>
            ) : users.length === 0 ? (
              <TableRow><TableCell colSpan={5}>No team members found.</TableCell></TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id} sx={{ height: 56 /* or 48, adjust as needed */ }}>
                  <TableCell>
                    {user.owner && (
                      <Tooltip title="Account Owner">
                        <StarIcon
                          sx={{
                            color: '#1976d2',
                            fontSize: 20,
                            mr: 1,
                            verticalAlign: 'middle',
                            position: 'relative',
                            top: '2px'
                          }}
                        />
                      </Tooltip>
                    )}
                    {!user.owner && user.chat_log_access_only && (
                      <Tooltip title="Chat Log Access Only">
                        <ChatOutlinedIcon
                          sx={{
                            color: 'text.secondary',
                            fontSize: 20,
                            mr: 1,
                            verticalAlign: 'middle',
                            position: 'relative',
                            top: '2px'
                          }}
                        />
                      </Tooltip>
                    )}
                    <span style={{ verticalAlign: 'middle' }}>{user.email}</span>
                  </TableCell>
                  <TableCell>{user.firstName}</TableCell>
                  <TableCell>{user.lastName}</TableCell>
                  <TableCell>
                    <Typography variant="body2" color={user.status === 'a' ? 'success.main' : user.status === 'd' ? 'error.main' : 'text.secondary'}>
                      {user.status === 'a' ? 'Active' : user.status === 'd' ? 'Disabled' : 'Invited'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" color="primary" onClick={() => handleEditOpen(user)} title="Edit">
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDeleteOpen(user)} title="Delete" disabled={user.owner}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                    {!user.owner && (
                      <Switch
                        checked={user.status === 'a'}
                        disabled={user.status === 'i'}
                        onChange={() => handleStatusToggle(user)}
                        color="primary"
                        sx={{ ml: 1 }}
                      />
                    )}
                    {/* Resend Invite button for invited users */}
                    {user.status === 'i' && !user.owner && (
                      <Button
                        size="small"
                        variant="outlined"
                        color="secondary"
                        sx={{ ml: 1 }}
                        onClick={() => handleResendInvite(user)}
                      >
                        Resend Invite
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Account Owner Note */}
      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          <Typography component="span" sx={{ color: '#1976d2', fontSize: '1.1em', mr: 0.5 }}>★</Typography> Account Owner
        </Typography>
        <Typography variant="body2" color="text.secondary">
          <ChatOutlinedIcon sx={{ fontSize: '1.1em', mr: 0.5, verticalAlign: 'text-bottom', color: 'text.secondary' }} />
          Chat Log Access Only
        </Typography>
      </Box>
      {/* Agency Users Table */}
      {agencyUsers.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">Agency Users</Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Email</TableCell>
                  <TableCell>First Name</TableCell>
                  <TableCell>Last Name</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agencyLoading ? (
                  <TableRow><TableCell colSpan={4}>Loading...</TableCell></TableRow>
                ) : (
                  agencyUsers.map((user) => (
                    <TableRow key={user.id} sx={{ height: 56 }}>
                      <TableCell>{user.email}</TableCell>
                      <TableCell>{user.firstName}</TableCell>
                      <TableCell>{user.lastName}</TableCell>
                      <TableCell>
                        <Typography variant="body2" color={user.status === 'a' ? 'success.main' : user.status === 'd' ? 'error.main' : 'text.secondary'}>
                          {user.status === 'a' ? 'Active' : user.status === 'd' ? 'Disabled' : 'Invited'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      {/* Invite Dialog */}
      <Dialog open={inviteDialogOpen} onClose={handleInviteClose}>
        <DialogTitle>Invite Team Member</DialogTitle>
        <DialogContent sx={{ minWidth: 400 }}>
          <TextField
            label="First Name"
            fullWidth
            margin="normal"
            value={inviteForm.firstName}
            onChange={e => setInviteForm({ ...inviteForm, firstName: e.target.value })}
          />
          <TextField
            label="Last Name"
            fullWidth
            margin="normal"
            value={inviteForm.lastName}
            onChange={e => setInviteForm({ ...inviteForm, lastName: e.target.value })}
          />
          <TextField
            label="Email Address"
            fullWidth
            margin="normal"
            value={inviteForm.email}
            onChange={e => setInviteForm({ ...inviteForm, email: e.target.value })}
            error={!!emailError}
            helperText={emailError}
          />
          <FormControl component="fieldset" margin="normal" sx={{ mt: 2 }}>
            <FormLabel component="legend">Access</FormLabel>
            <RadioGroup
              value={inviteForm.accessType}
              onChange={e => setInviteForm({ ...inviteForm, accessType: e.target.value })}
            >
              <FormControlLabel value="standard" control={<Radio color="primary" />} label="Standard Team Member" />
              <FormControlLabel value="owner" control={<Radio color="primary" />} label="Account Owner" />
              <FormControlLabel
                value="chatLogOnly"
                control={<Radio color="primary" />}
                label="Chat Log Access Only"
              />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleInviteClose}>Cancel</Button>
          <Button onClick={handleInviteSubmit} variant="contained" color="primary">Send Invitation</Button>
        </DialogActions>
      </Dialog>
      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose}>
        <DialogTitle>Edit Team Member</DialogTitle>
        <DialogContent sx={{ minWidth: 400 }}>
          <TextField
            label="Username/Email"
            fullWidth
            margin="normal"
            value={editForm.email}
            InputProps={{ readOnly: true }}
          />
          <TextField
            label="First Name"
            fullWidth
            margin="normal"
            value={editForm.firstName}
            onChange={e => setEditForm({ ...editForm, firstName: e.target.value })}
          />
          <TextField
            label="Last Name"
            fullWidth
            margin="normal"
            value={editForm.lastName}
            onChange={e => setEditForm({ ...editForm, lastName: e.target.value })}
          />
          {editForm.email !== currentUser?.email && (
            <FormControl component="fieldset" margin="normal" sx={{ mt: 2 }}>
              <FormLabel component="legend">Access</FormLabel>
              <RadioGroup
                value={editForm.accessType}
                onChange={handleAccessTypeRadioChange}
              >
                <FormControlLabel value="standard" control={<Radio color="primary" />} label="Standard Team Member" />
                <FormControlLabel value="owner" control={<Radio color="primary" />} label="Account Owner" />
                <FormControlLabel
                  value="chatLogOnly"
                  control={<Radio color="primary" />}
                  label="Chat Log Access Only"
                />
              </RadioGroup>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained" color="primary">Save Changes</Button>
        </DialogActions>
      </Dialog>

      {/* Owner Change Confirmation Dialog */}
      <Dialog open={ownerChangeDialogOpen} onClose={handleOwnerChangeCancel}>
        <DialogTitle>Confirm Owner Status Change</DialogTitle>
        <DialogContent>
          <Typography>
            {pendingAccessType === 'owner'
              ? 'Are you sure you want to promote this user to Account Owner?'
              : 'Are you sure you want to remove Account Owner status from this user?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleOwnerChangeCancel}>Cancel</Button>
          <Button onClick={handleOwnerChangeConfirm} color="primary" variant="contained">Confirm</Button>
        </DialogActions>
      </Dialog>
      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteClose}>
        <DialogTitle>Confirm Removal</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to remove {selectedUser?.firstName || selectedUser?.email} from the team?
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose}>Cancel</Button>
          <Button onClick={handleDeleteSubmit} variant="contained" color="error">Remove</Button>
        </DialogActions>
      </Dialog>
      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={alert?.message || ''}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Paper>
  );
};

export default TeamManagement;
